import { useCallback } from 'react';
import { usePlatformAuth } from '../contexts/AuthContext';
import { Account, DocumentContent, ProcessingInput, TaskRequest, UserRequest } from '../types';
import { api } from '../services/api';

export const useDocumentGeneration = (
  selectedTemplate: string,
  templates: any[],
  attachments: DocumentContent[],
  currentAccount: Account,
  selectedPatientId: string,
  setIsLoading: (loading: boolean) => void,
  setCurrentStage: (stage: number) => void,
  setError: (error: string | null) => void,
  setFinalResult: (result: any | null) => void,
  setIsProcessingComplete: (complete: boolean) => void,
  onRequestCreated: (request: UserRequest) => void
) => {
  const auth = usePlatformAuth();

  const handleGenerate = useCallback(async () => {
    if (!auth.isAuthenticated || !selectedTemplate || attachments.length === 0 || !currentAccount) {
      return;
    }
    setIsLoading(true);
    setCurrentStage(1);
    setError(null);
    setFinalResult(null); // Clear previous results
    setIsProcessingComplete(false);
    
    try {
      // Prepare the request immediately
      const processingInput: ProcessingInput = {
        id: selectedTemplate,
        sourceType: 'api',
        body: 'Generate document from uploaded images',
        subject: 'Document Generation Request',
        from: 'user@example.com',
        to: ['system@example.com'],
        attachments: attachments,
        metadata: {},
      };
      const taskRequest: TaskRequest = {
        selectedTaskDefinitionId: selectedTemplate,
        account: currentAccount,
        processingInput: processingInput,
      };

      // Note: ProcessingContext removed - requests are now tracked via UserRequest

      // Get task type from selected template
      const selectedTemplateObj = templates.find(t => t.id === selectedTemplate);
      const taskType = selectedTemplateObj?.taskType || 'DocumentPopulation';

      // Ensure minimum 1.5 seconds of "Queueing Request..." display
      const startTime = Date.now();
      
      // Stage 2: Execute task (this is where the real work happens)
      setCurrentStage(2);
      const userRequest = await api.executeTask(taskRequest, selectedPatientId || 'No Patient Selected', taskType);
      
      // Calculate remaining time to reach 1.5 seconds minimum
      const elapsedTime = Date.now() - startTime;
      const remainingTime = Math.max(0, 1500 - elapsedTime);
      
      if (remainingTime > 0) {
        await new Promise(resolve => setTimeout(resolve, remainingTime));
      }
      
      // Stage 3: Complete (brief moment to show completion)
      setCurrentStage(3);
      
      // Notify parent component about the new request (this will trigger the animation)
      onRequestCreated(userRequest);
      
      // Note: setIsLoading(false) will be called by the parent after animation completes
    } catch (err) {
      setIsLoading(false);
      setCurrentStage(1);
      setError('Failed to start document generation. Please try again.');
      console.error('Error generating document:', err);
    }
  }, [auth.isAuthenticated, selectedTemplate, templates, attachments, currentAccount, selectedPatientId, setIsLoading, setCurrentStage, setError, setFinalResult, setIsProcessingComplete, onRequestCreated]);

  const handleCancelGeneration = useCallback(() => {
    setIsLoading(false);
    setCurrentStage(1);
    setError('Document generation was cancelled.');
  }, [setIsLoading, setCurrentStage, setError]);

  return {
    handleGenerate,
    handleCancelGeneration,
  };
}; 